import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToggleLocalMute } from 'amazon-chime-sdk-component-library-react'
import useBrowserVideoPreview from '@/hooks/useBrowserVideoPreview'
import { useParticipantStatus } from '@/hooks/useParticipantStatus'
import { mockAppointment } from '@/mocks/mockAppointment'
import { MeetingHeader } from '@/components/molecules/Header/MeetingHeader'
import { WaitingRoomCard } from '@/components/organisms/WaitingRoomCard'
import { ConsentCard } from '@/components/molecules/ConsentCard'
import { AppTemplate } from '@/components/templates/AppTemplate/AppTemplate'
import { useDeviceSelection } from '@/hooks/useDeviceSelection'
import { BrowserVideoPreview } from '@/components/atoms/BrowserVideoPreview/BrowserVideoPreview'

/**
 * Demo version of WaitingRoom page
 * Works without backend - uses mock data
 * Access via /appointment/demo
 */
export const WaitingRoomDemo = () => {
  const navigate = useNavigate()
  const [isVideoActive, setIsVideoActive] = useState(true)

  // Real device selection (doesn't require backend)
  const deviceSelection = useDeviceSelection({ autoSelect: true })

  // Real audio toggle (doesn't require backend)
  const { muted, toggleMute } = useToggleLocalMute()

  // Check if other participant is present (mocked for demo)
  const { isParticipantPresent } = useParticipantStatus()

  // Use static mock status (no automatic refresh in demo mode)
  const timeStatus = mockAppointment.timeStatus

  // Browser-native video preview (doesn't require Chime session)
  const {
    stream,
    error: videoError,
    isLoading: isVideoLoading
  } = useBrowserVideoPreview({
    videoDeviceId: deviceSelection.selectedVideoInput,
    isVideoEnabled: isVideoActive
  })

  const handleJoinMeeting = async () => {
    // Simulate joining delay
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Navigate to demo meeting page
    navigate('/meeting/demo')
  }

  const handleToggleVideo = () => {
    setIsVideoActive((prev) => !prev)
  }

  const noCameraAvailable = deviceSelection.videoInputs.length === 0
  const noMicrophoneAvailable = deviceSelection.audioInputs.length === 0

  const participant = mockAppointment.interlocutor

  if (!participant) {
    return (
      <AppTemplate header={<MeetingHeader showVideocallTag />}>
        <div className="flex items-center justify-center p-8">
          <p className="text-secondary">Aucun participant trouvé pour ce rendez-vous (demo).</p>
        </div>
      </AppTemplate>
    )
  }

  return (
    <AppTemplate header={<MeetingHeader showVideocallTag />}>
      <div className="flex w-full max-w-7xl flex-col gap-6">
        {/* Main Waiting Room Card */}
        <WaitingRoomCard
          // Video Setup Props
          isVideoActive={isVideoActive && !!deviceSelection.selectedVideoInput}
          isMicrophoneActive={!muted}
          noCameraAvailable={noCameraAvailable}
          noMicrophoneAvailable={noMicrophoneAvailable}
          onToggleVideo={handleToggleVideo}
          onToggleMicrophone={toggleMute}
          videoInputs={deviceSelection.videoInputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          audioInputs={deviceSelection.audioInputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          audioOutputs={deviceSelection.audioOutputs.map((device) => ({
            id: device.id,
            label: device.label
          }))}
          selectedVideoInput={deviceSelection.selectedVideoInput}
          selectedAudioInput={deviceSelection.selectedAudioInput}
          selectedAudioOutput={deviceSelection.selectedAudioOutput}
          onVideoInputChange={(id: string) => void deviceSelection.selectVideoInput({ id })}
          onAudioInputChange={(id: string) => void deviceSelection.selectAudioInput({ id })}
          onAudioOutputChange={(id: string) => void deviceSelection.selectAudioOutput({ id })}
          previewComponent={<BrowserVideoPreview stream={stream} isLoading={isVideoLoading} error={videoError} />}
          // Appointment Info Props
          participantName={participant.firstName}
          participantAvatar={participant.avatar ?? undefined}
          scheduledDate={mockAppointment.from}
          isParticipantPresent={isParticipantPresent}
          conversationId={mockAppointment.conversationId}
          timeStatus={timeStatus}
          onJoinMeeting={handleJoinMeeting}
        />

        {/* Consent Card */}
        <ConsentCard />
      </div>
    </AppTemplate>
  )
}
